import React, { useCallback } from "react";
import { TFunction } from "i18next";
import { ProductWriteOffInput } from "@me-team/host/main/graphql/types";
import {
    InterfaceProductWithTechMap,
    ProductListInterfaceForWriteOffPage,
    CustomProductFromTechMapInterface,
    ProductToWriteOffCustomInterface,
} from "../../../../interfaces/interfaces";

type ProductValidationErrors = {
    storageError: string | null;
    qtyError: string | null;
};

const isValidatableField = (field: keyof ProductWriteOffInput): field is "storage" | "quantity" | "unit" =>
    field === "storage" || field === "quantity" || field === "unit";

export const useWriteOffProducts = (
    setWriteOffProducts: React.Dispatch<React.SetStateAction<ProductToWriteOffCustomInterface[]>>,
    setCombinedProducts: React.Dispatch<
        React.SetStateAction<{
            productFromTechMap: InterfaceProductWithTechMap[];
            addedProductToWriteOff: ProductListInterfaceForWriteOffPage[];
        }>
    >,
    translate: TFunction<"translation">
) => {
    const getFieldError = (field: "storage" | "quantity", product: ProductWriteOffInput): string | null => {
        if (field === "storage") {
            return !product.storage ? translate("Select warehouse") : null;
        }
        if (field === "quantity") {
            return product.quantity !== null && (product.isZeroAccept ? product.quantity >= 0 : product.quantity > 0)
                ? null
                : translate("Specify quantity");
        }
        return null;
    };

    const hasAnyError = (errors: ProductValidationErrors): boolean => Boolean(errors.storageError || errors.qtyError);

    const updateErrors = (
        prevErrors: ProductValidationErrors,
        field: keyof ProductWriteOffInput,
        product: ProductWriteOffInput
    ): ProductValidationErrors => {
        if (field === "quantity") {
            return {
                ...prevErrors,
                qtyError: getFieldError("quantity", product),
            };
        }
        if (field === "storage") {
            return {
                ...prevErrors,
                storageError: getFieldError("storage", product),
            };
        }
        return prevErrors;
    };

    const clearQtyErrorIfNeeded = (
        errors: ProductValidationErrors,
        changedField: keyof ProductWriteOffInput,
        quantity: number
    ): ProductValidationErrors => {
        const isQuantityFilled: boolean = !!quantity;
        if ((changedField === "storage" || changedField === "unit") && isQuantityFilled && errors.qtyError) {
            return {
                ...errors,
                qtyError: null,
            };
        }
        return errors;
    };

    const onChangeProductsToWriteOff = useCallback(
        (
            productToWriteOff: ProductToWriteOffCustomInterface,
            changedField: keyof ProductWriteOffInput,
            isFromTechMap?: boolean,
            techMapId?: number
        ) => {
            setWriteOffProducts((prevState) => {
                const updated = [...prevState];

                const index = prevState.findIndex((product) => {
                    if (isFromTechMap && techMapId) {
                        return (
                            product.product === productToWriteOff.product &&
                            product.techMapId === productToWriteOff.techMapId
                        );
                    }
                    return product.product === productToWriteOff.product;
                });

                if (index === -1) {
                    updated.push(productToWriteOff);
                } else {
                    updated[index] = productToWriteOff;
                }

                return updated;
            });

            if (!isValidatableField(changedField)) return;

            setCombinedProducts((prevState) => {
                if (isFromTechMap && techMapId) {
                    const updatedProductFromTechMap = prevState.productFromTechMap.map((techMap) => {
                        if (techMapId !== techMap.techMapId) return techMap;

                        const updatedTechMapProductInfo = techMap.techMapProductInfo.map(
                            (product: CustomProductFromTechMapInterface) => {
                                if (product.product.id !== productToWriteOff.product) return product;

                                let updatedErrors = updateErrors(product.errors, changedField, productToWriteOff);
                                updatedErrors = clearQtyErrorIfNeeded(
                                    updatedErrors,
                                    changedField,
                                    productToWriteOff.quantity
                                );

                                return {
                                    ...product,
                                    errors: updatedErrors,
                                    hasError: hasAnyError(updatedErrors),
                                };
                            }
                        );

                        return {
                            ...techMap,
                            techMapProductInfo: updatedTechMapProductInfo,
                        };
                    });

                    return {
                        ...prevState,
                        productFromTechMap: updatedProductFromTechMap,
                    };
                } else {
                    const updatedAddedProducts = prevState.addedProductToWriteOff.map((product) => {
                        if (product.id === productToWriteOff.product) {
                            let updatedErrors = updateErrors(product.errors, changedField, productToWriteOff);
                            updatedErrors = clearQtyErrorIfNeeded(
                                updatedErrors,
                                changedField,
                                productToWriteOff.quantity
                            );

                            return {
                                ...product,
                                errors: updatedErrors,
                                hasError: hasAnyError(updatedErrors),
                            };
                        }
                        return product;
                    });

                    return {
                        ...prevState,
                        addedProductToWriteOff: updatedAddedProducts,
                    };
                }
            });
        },
        [setWriteOffProducts, setCombinedProducts, translate, updateErrors]
    );

    return { onChangeProductsToWriteOff };
};
